<template>
  <div>
    <div v-if="localGroups.length > 0">
      <h3 class="mb-4">Input data</h3>

      <!--Treatment History-->
      <div>
        <p class="h5 my-4">Treatment History</p>

        <div class="mb-2 font-italic" v-if="!localTreatmentHistory.length">
          No Treatment History yet
        </div>

        <div
          class="mb-3 history-line"
          v-for="(history, index) in localTreatmentHistory"
        >
          <div class="history-line--label" style="padding-left: 0">
            {{ map[index].Line }} <br />
            <span style="opacity: 0.6;">{{ map[index].Text }}</span>
          </div>

          <div class="history-line--treat">
            <v-select
              id="toolSelector"
              :options="availableTreatments"
              label="name"
              code="id"
              placeholder="Select Treatment"
              :value="history"
              @input="value => updateHistory(value, history, index)"
            >
            </v-select>
          </div>

          <div class="form-group history-line--tolerated mb-0" v-if="!hideTreatmentResultCheckbox">
            <div class="mr-2 custom-control custom-checkbox">
              <input
                type="checkbox"
                :id="`WellTolerated_${index}`"
                class="custom-control-input"
                v-model="history.wellTolerated"
              />
              <label
                class="custom-control-label"
                :for="`WellTolerated_${index}`"
              >
                Well Tolerated
              </label>
            </div>
            <div class="mr-2 custom-control custom-checkbox">
              <input
                  type="checkbox"
                  :id="`WasEffective_${index}`"
                  class="custom-control-input"
                  v-model="history.wasEffective"
              />
              <label
                  class="custom-control-label"
                  :for="`WasEffective_${index}`"
              >
                Was Effective
              </label>
            </div>
          </div>
        </div>

        <div>
          <span
            v-if="localTreatmentHistory.length < 5"
            class="link"
            :class="!canAddNewHistory ? 'disable-link' : ''"
            v-on:click="addHistory"
            >+ Add more Line</span
          >
        </div>
      </div>
      <!--//Treatment History-->

      <!--Input Data-->
      <app-group
        v-for="(group, index) of localGroups"
        :key="index"
        :group="group"
      />
      <!--//Input Data-->
    </div>

    <div v-else>
      <div class="col">
        <h1 class="text-center">Experts opinion is not completed yet</h1>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Prop, Watch, Component, Vue } from "vue-property-decorator";
import { Answer } from "@/entities/questionaire/answers/Answer";
import { Treatment } from "@/entities/Treatment";
import { Group } from "@/entities/questionaire/Group";
import { HttpHandler } from "@/http/HttpHandler";
import AppGroup from "@/components/questionnaire/AppGroup.vue";
import AppDiseaseTreeSelector from "@/components/AppDiseaseTreeSelector.vue";
import "vue-select/dist/vue-select.css";
import store from "@/store/index";

@Component({
  components: {
    AppGroup,
    AppDiseaseTreeSelector
  }
})
export default class AppQuestionnaire extends Vue {
  private httpHandler: HttpHandler = new HttpHandler(store);
  private error: boolean = false;

  @Prop()
  branchId!: number | null;
  private localBranchId: number | null = null;

  @Prop()
  groups!: Group[] | null;
  private localGroups: Group[] = [];

  @Prop()
  answers!: Answer[] | null;
  private localAnswers: Answer[] = [];

  @Prop()
  treatmentHistory!: Treatment[] | null;
  private localTreatmentHistory: Treatment[] = [];

  @Prop()
  hideTreatmentResultCheckbox: boolean = false;

  private availableTreatments: Treatment[] = [];

  private map: any[] = [
    {
      Line: "1st Treatment",
      Text: ""
    },
    {
      Line: "2nd Treatment",
      Text: ""
    },
    {
      Line: "3rd Treatment",
      Text: ""
    },
    {
      Line: "4th Treatment",
      Text: ""
    },
    {
      Line: "5th Treatment",
      Text: ""
    }
  ];

  async created(): Promise<void> {
    this.localBranchId = this.branchId;
    this.localGroups = [...(this.groups ?? [])];
    this.localAnswers = [...(this.answers ?? [])];
    this.localTreatmentHistory = [...(this.treatmentHistory ?? [])];

    await this.updateGroups();
  }

  mounted() {
    this.$events.on("answerChanged", this.setAnswer);
    this.$events.on("throwError", id => {
      this.error = true;
    });
  }

  beforeDestroy() {
    this.$events.off("answerChanged");
    this.$events.off("throwError");
  }

  setAnswer(answer: Answer): void {
    const newAnswers = [...this.localAnswers];
    const index = newAnswers.findIndex(x => x.PropertyId === answer.PropertyId);

    if (index > -1) newAnswers.splice(index, 1, answer);
    else newAnswers.push(answer);

    this.localAnswers = newAnswers.filter(x => !x.isEmpty());
  }

  get canAddNewHistory() {
    return !this.localTreatmentHistory.find(treat => treat.id === 0);
  }

  addHistory() {
    if (this.canAddNewHistory) {
      this.localTreatmentHistory.push({
        id: 0,
        name: "Select Treatment",
        wellTolerated: undefined,
        wasEffective: undefined,
        ignoreTool: false
      });
    }
  }

  updateHistory(value: Treatment, history: Treatment, index: number) {
    if (!value) {
      this.localTreatmentHistory.splice(index, 1);
    } else {
      history.id = value.id;
      history.name = value.name;
    }
  }

  async updateGroups() {
    this.localGroups = this.localBranchId
      ? await this.httpHandler.getGroupsByBranchId(this.localBranchId)
      : [];
    this.localAnswers = [];

    let branchTreatments = this.localBranchId
      ? await this.httpHandler.getToolsByBranchId(this.localBranchId)
      : [];

    this.availableTreatments = branchTreatments.map(b => {
      return new Treatment(b.id, b.name);
    });

    this.localTreatmentHistory = [];
  }

  @Watch("branchId")
  onBranchIdChanged() {
    this.localBranchId = this.branchId;
  }
  @Watch("localBranchId")
  onLocalBranchIdChanged() {
    this.updateGroups();
    this.$emit("update:branchId", this.localBranchId);
  }
  @Watch("groups")
  onGroupsChanged() {
    this.localGroups = this.groups ?? [];
  }
  @Watch("localGroups")
  onLocalGroupsChanged() {
    this.$emit("update:groups", this.localGroups);
  }

  @Watch("answers")
  onAnswersChanged() {
    this.localAnswers = this.answers ?? [];
  }

  @Watch("localAnswers")
  onLocalAnswersChanged() {
    this.$emit("update:answers", this.localAnswers);
  }

  @Watch("localTreatmentHistory", { deep: true })
  onLocalHistoryChanged() {
    this.$emit("update:treatmentHistory", this.localTreatmentHistory);
  }
}
</script>

<style lang="scss" scoped>
.history-line {
  display: flex;
  align-items: center;
  flex: 0 0 66.666667%;
  max-width: 66.666667%;

  .history-line--label {
    flex: 2;
    margin-right: 10px;
  }

  .history-line--treat {
    flex: 5;
    min-width: 40%;
    margin-right: 10px;
  }

  .history-line--tolerated {
    flex: 3;
  }

  .history-line--remove {
  }
}

.link {
  color: #007bff;
  cursor: pointer;
}

.disable-link {
  color: grey;
  cursor: unset;
}
</style>
